import {
  Checkbox,
  IconButton,
  LinearProgress,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import React, { FC, memo } from 'react';
import { AlternatingColorTableRow } from 'src/components';
import { formatCurrency } from 'src/utils';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { VoucherProduct } from 'src/redux/slices/voucher/types';
import { slices, useAppSelector } from 'src/redux';
import useResolution from 'src/hooks/useResolution';
import { AlternateRowContainer } from 'src/views/product/component/AlternateRowContainer';
import { TextRow } from 'src/views/product/component/TextRow';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { colors } from 'src/constants/colors';

const useStyles = makeStyles(() => ({
  table: {
    border: '1px solid #a4abba'
  },
  tableCell: {
    textAlign: 'center',
    borderBottom: '1px solid #a4abba'
  },
  tableRow: {
    borderBottom: '1px solid #a4abba'
  }
}));

const { selectors: voucherSelectors } = slices.voucher;

interface Props {
  selectedProducts: VoucherProduct[];
  toggleSelectProductIds: (p: VoucherProduct) => void;
}

const Component: FC<Props> = ({ selectedProducts, toggleSelectProductIds }) => {
  const classes = useStyles();
  const { isMobile } = useResolution();

  const products = useAppSelector(voucherSelectors.selectProducts);
  const isProductsLoading = useAppSelector(
    voucherSelectors.selectIsProductsLoading
  );

  return isMobile ? (
    <PerfectScrollbar className={classes.table} style={{ height: '100%' }}>
      {isProductsLoading ? <LinearProgress /> : null}
      {products?.[0]
        ? products?.map((p, ind) => (
            <AlternateRowContainer key={ind} index={ind}>
              <div style={{ padding: 10, position: 'relative' }}>
                <TextRow
                  label=""
                  small
                  component={
                    <IconButton
                      style={{
                        cursor: 'pointer',
                        position: 'absolute',
                        borderRadius: '50px',
                        padding: 3,
                        backgroundColor: colors.common.white,
                        top: 10,
                        right: 10
                      }}
                      onClick={() => toggleSelectProductIds(p)}
                    >
                      <CheckCircleOutlineIcon
                        color={
                          selectedProducts?.find((i) => i.id === p.id)
                            ? 'primary'
                            : 'disabled'
                        }
                      />
                    </IconButton>
                  }
                />
                <TextRow
                  label="Name"
                  small
                  component={
                    <div style={{ textAlign: 'center' }}>
                      <Typography style={{ fontSize: '.8em' }}>
                        {p.name}
                      </Typography>
                    </div>
                  }
                />
                <TextRow label="Category" small value={p?.category} />
                <TextRow
                  label="Dealers Price"
                  value={formatCurrency(p?.dealers_price)}
                  small
                />
                <TextRow
                  label="Retail Price"
                  small
                  value={formatCurrency(p.retail_price)}
                />
                <TextRow
                  label="Discounted Price"
                  small
                  value={formatCurrency(p.discounted_price)}
                />
                <TextRow
                  label="Margin"
                  small
                  value={p?.margin}
                  noBorderBottom
                />
              </div>
            </AlternateRowContainer>
          ))
        : null}
    </PerfectScrollbar>
  ) : (
    <PerfectScrollbar style={{ height: '100%' }}>
      {isProductsLoading ? <LinearProgress /> : null}
      <Table className={classes.table} stickyHeader>
        <TableHead>
          <TableRow className={classes.tableRow}>
            {/* Intended blank cell below for checkbox column */}
            <TableCell className={classes.tableCell}></TableCell>
            {/* TODO: have consitent width for name cell */}
            <TableCell className={classes.tableCell}>Product Name</TableCell>
            <TableCell className={classes.tableCell}>Category</TableCell>
            <TableCell className={classes.tableCell}>Dealers Price</TableCell>
            <TableCell className={classes.tableCell}>Retail Price</TableCell>
            <TableCell className={classes.tableCell}>
              Discounted Price
            </TableCell>
            <TableCell className={classes.tableCell}>Margin</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products?.[0] && !isProductsLoading
            ? products.map((p) => {
                return (
                  <AlternatingColorTableRow key={p.id}>
                    <TableCell className={classes.tableCell}>
                      <Checkbox
                        checked={!!selectedProducts.find((i) => i.id === p.id)}
                        onClick={() => toggleSelectProductIds(p)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {p.name}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {p.category}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {formatCurrency(p.dealers_price)}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {formatCurrency(p.retail_price)}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {formatCurrency(p.discounted_price)}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {p.margin}
                    </TableCell>
                  </AlternatingColorTableRow>
                );
              })
            : null}
        </TableBody>
      </Table>
    </PerfectScrollbar>
  );
};

export const SelectProductsTable = memo(Component);
